import { useEffect, useMemo, useState } from 'react';
import { Radio } from 'antd';
import { FiDatabase, FiFileText, FiPlus } from 'react-icons/fi';
import {
  getDematOptionValue,
  hasUsableCmrDemat,
  parseDematOptionKey,
} from '../../../utils/dematStepPrefill';

function formatDematSummary(demat) {
  if (!demat) return '';
  return [demat.demat_account_no || '', demat.dp_name || '', demat.status || '']
    .filter(Boolean)
    .join(' · ');
}

function defaultSelectedValue(existingDemats, hasCmr) {
  if (existingDemats.length > 0) return getDematOptionValue(existingDemats[0]);
  if (hasCmr) return 'cmr';
  return 'new';
}

function OptionRow({ value, groupValue, icon: Icon, iconClass, title, subtitle }) {
  const selected = groupValue === value;
  return (
    <label
      className={`flex w-full cursor-pointer gap-4 rounded-xl border px-5 py-4 transition-colors ${
        selected ? 'border-green-600 bg-green-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <Radio value={value} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-center gap-2.5">
          <Icon className={`h-4 w-4 shrink-0 ${iconClass}`} aria-hidden />
          <span className="text-sm font-semibold text-gray-900">{title}</span>
        </div>
        {subtitle ? <p className="text-xs leading-relaxed text-gray-500">{subtitle}</p> : null}
      </div>
    </label>
  );
}

/**
 * Opens when there are multiple saved demats or a CMR/saved conflict.
 * Lets the admin pick which source to use for the demat form.
 */
export default function DematSourceChoiceModal({
  open,
  existingDemats,
  cmrFields,
  hasCmrDemat: hasCmrDematProp,
  onUseExisting,
  onUseCmr,
  onUseNew,
}) {
  const hasCmrDemat = hasCmrDematProp ?? hasUsableCmrDemat(cmrFields);

  const [selectedValue, setSelectedValue] = useState(() =>
    defaultSelectedValue(existingDemats, hasCmrDemat)
  );

  useEffect(() => {
    if (!open) return;
    setSelectedValue(defaultSelectedValue(existingDemats, hasCmrDemat));
  }, [open, existingDemats, hasCmrDemat]);

  const selectedOptionKey = parseDematOptionKey(selectedValue);
  const selectedDemat = useMemo(
    () => existingDemats.find((d) => String(d.optionKey) === String(selectedOptionKey)),
    [existingDemats, selectedOptionKey]
  );

  const isSavedSelected = Boolean(selectedOptionKey);
  const isCmrSelected = selectedValue === 'cmr';

  const handleContinue = () => {
    if (isSavedSelected && selectedDemat) onUseExisting(selectedDemat);
    else if (isCmrSelected) onUseCmr();
    else onUseNew();
  };

  const continueLabel = isSavedSelected
    ? 'Continue with saved demat'
    : isCmrSelected
      ? 'Continue with CMR details'
      : 'Continue with blank form';

  if (!open) return null;

  const multipleSaved = existingDemats.length >= 2;
  const hasSaved = existingDemats.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="demat-source-title"
    >
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-8 shadow-xl">
        <h2 id="demat-source-title" className="text-lg font-semibold text-gray-900">
          Which demat account should we use?
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-gray-500">
          {multipleSaved
            ? `We found ${existingDemats.length} saved demat accounts${hasCmrDemat ? ' and CMR data' : ''}. Pick one to prefill.`
            : 'A conflict was detected between the saved demat and CMR data. Pick the one to use.'}
        </p>

        <div className="pt-5">
          <Radio.Group
            className="block w-full"
            value={selectedValue}
            onChange={(e) => setSelectedValue(e.target.value)}
          >
            <div className="flex w-full flex-col gap-4">
              {hasSaved && multipleSaved ? (
                <div className="flex flex-col gap-4">
                  <p className="px-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Saved on file
                  </p>
                  {existingDemats.map((demat) => (
                    <OptionRow
                      key={demat.optionKey}
                      value={getDematOptionValue(demat)}
                      groupValue={selectedValue}
                      icon={FiDatabase}
                      iconClass="text-green-600"
                      title="Saved demat account"
                      subtitle={formatDematSummary(demat)}
                    />
                  ))}
                </div>
              ) : null}

              {hasSaved && !multipleSaved ? (
                <OptionRow
                  value={getDematOptionValue(existingDemats[0])}
                  groupValue={selectedValue}
                  icon={FiDatabase}
                  iconClass="text-green-600"
                  title="Saved on file"
                  subtitle={formatDematSummary(existingDemats[0])}
                />
              ) : null}

              {hasCmrDemat ? (
                <OptionRow
                  value="cmr"
                  groupValue={selectedValue}
                  icon={FiFileText}
                  iconClass="text-emerald-600"
                  title="From CMR document"
                  subtitle={cmrFields?.demat_account_no || 'Extracted from upload'}
                />
              ) : null}

              <OptionRow
                value="new"
                groupValue={selectedValue}
                icon={FiPlus}
                iconClass="text-gray-500"
                title="Enter a new demat manually"
                subtitle="No prefill — you type the demat number and upload CMR."
              />
            </div>
          </Radio.Group>
        </div>

        <div className="mt-7">
          <button
            type="button"
            onClick={handleContinue}
            disabled={isSavedSelected && !selectedDemat}
            className="w-full rounded-lg bg-green-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {continueLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
